import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Navigate, useSearchParams } from "react-router-dom";
import { AUTH, NAV } from "@/constants/testIds";
import { languages } from "@/lib/i18n";
import { api } from "@/lib/api";
import { Sparkles, TrendingUp, ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";

export default function Login() {
  const { status, setUser, setStatus } = useAuth();
  const { t, lang, setLang } = useLang();
  const [searchParams] = useSearchParams();
  const [errorCode] = useState(searchParams.get("error"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loginError, setLoginError] = useState("");

  useEffect(() => {
    document.title = `${lang === "de" ? "Anmelden" : "Sign in"} · Rankly`;
  }, [lang]);

  if (status === "authed") return <Navigate to="/dashboard" replace />;

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const formatDetail = (detail) => {
    if (!detail) return t("login_error_generic");
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
    return String(detail);
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    if (!email || !password) {
      setLoginError(t("login_error_missing_fields"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/auth/login", { email: email.trim(), password });
      setUser(res.data.user);
      setStatus("authed");
    } catch (err) {
      setLoginError(formatDetail(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid={AUTH.loginPageRoot}
      className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]"
    >
      {/* Left — Brand / hero */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-[#0A0A0A] text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.14]"
          style={{
            backgroundImage:
              "linear-gradient(hsl(0 0% 100% / 0.12) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100% / 0.12) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="relative flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-white text-black grid place-items-center font-display font-bold">
            R
          </div>
          <span className="font-display text-xl font-semibold tracking-tight">Rankly</span>
        </div>

        <div className="relative rk-fade-in">
          <div className="text-xs uppercase tracking-[0.18em] text-neutral-400 mb-4 font-mono">
            SEO · Site Audit · Multi-Shop
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05] max-w-lg">
            {t("tagline")}
          </h1>
          <p className="mt-5 text-neutral-400 max-w-md text-sm leading-relaxed">
            {t("login_subtitle")}
          </p>

          <ul className="mt-8 space-y-3 text-sm text-neutral-300 max-w-md">
            <li className="flex items-start gap-3">
              <TrendingUp className="h-4 w-4 mt-0.5 text-[hsl(217_91%_60%)]" />
              <span>{t("hero_bullet_1")}</span>
            </li>
            <li className="flex items-start gap-3">
              <ShieldCheck className="h-4 w-4 mt-0.5 text-[hsl(38_94%_60%)]" />
              <span>{t("hero_bullet_2")}</span>
            </li>
            <li className="flex items-start gap-3">
              <Sparkles className="h-4 w-4 mt-0.5 text-[hsl(152_65%_50%)]" />
              <span>{t("hero_bullet_3")}</span>
            </li>
          </ul>
        </div>

        <div className="relative font-mono text-xs text-neutral-500 grid grid-cols-3 gap-4 max-w-md">
          {["pedalo24.com", "366transfer.com", "redseadreams.com"].map((d) => (
            <div key={d} className="border border-neutral-800 rounded-md px-3 py-2">
              <div className="text-neutral-500">Shop</div>
              <div className="text-neutral-200 truncate">{d}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Right — Login card */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm rk-fade-in">
          <div className="flex items-center justify-end mb-8">
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger
                data-testid={NAV.languageSwitcher}
                className="w-32 h-9 font-mono text-xs"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map((l) => (
                  <SelectItem key={l.code} value={l.code} className="font-mono text-xs">
                    {l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="lg:hidden mb-8 flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-foreground text-background grid place-items-center font-display font-bold">
              R
            </div>
            <span className="font-display text-xl font-semibold">Rankly</span>
          </div>

          <h2 className="font-display text-3xl font-semibold tracking-tight">
            {t("login_title")}
          </h2>
          <p className="text-sm text-muted-foreground mt-2">{t("login_subtitle")}</p>

          {errorCode === "forbidden" && (
            <div
              data-testid="login-error-forbidden"
              className="mt-6 flex gap-3 rounded-md border border-destructive/40 bg-destructive/5 p-3"
            >
              <ShieldAlert className="h-5 w-5 shrink-0 text-destructive mt-0.5" />
              <div className="text-sm">
                <div className="font-medium text-destructive">
                  {t("login_error_forbidden_title")}
                </div>
                <div className="text-muted-foreground mt-1">
                  {t("login_error_forbidden_desc")}
                </div>
              </div>
            </div>
          )}

          <Tabs defaultValue="password" className="mt-8">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="password" data-testid="login-tab-password">
                {t("login_tab_password")}
              </TabsTrigger>
              <TabsTrigger value="google" data-testid="login-tab-google">
                Google
              </TabsTrigger>
            </TabsList>

            <TabsContent value="password" className="mt-6">
              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="login-email">{t("login_email_label")}</Label>
                  <Input
                    id="login-email"
                    type="email"
                    autoComplete="username"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="login-password">{t("login_password_label")}</Label>
                  <Input
                    id="login-password"
                    type="password"
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                  />
                </div>
                {loginError && (
                  <div
                    data-testid="login-error-inline"
                    className="text-sm text-destructive flex gap-2 items-start"
                  >
                    <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{loginError}</span>
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-11 gap-2"
                  data-testid="login-submit-button"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {t("login_submit")}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="google" className="mt-6">
              <p className="text-sm text-muted-foreground mb-4">
                {t("login_google_hint")}
              </p>
              <Button
                data-testid={AUTH.googleLoginBtn}
                onClick={handleGoogle}
                className="w-full h-11 gap-3 rounded-md"
                variant="default"
              >
                <GoogleIcon className="h-5 w-5" />
                {t("login_with_google")}
              </Button>
            </TabsContent>
          </Tabs>

          <div className="mt-8 text-xs text-muted-foreground font-mono">
            v0.2 · secure email/password auth
          </div>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09A6.98 6.98 0 0 1 5.47 12c0-.72.13-1.43.36-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.77.43 3.45 1.18 4.93l3.66-2.84z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
      />
    </svg>
  );
}
